import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import {
    formatDollar,
    formatPercent,
    formatPercentNoMultiply,
    noFormatRaw
} from '../Financial/PanelCompanyYearly/PanelElements/Formatters';
import { RowLabel } from '../Financial/PanelCompanyYearly/PanelElements/RowLable/RowLable';
import { RowValue } from '../Financial/PanelCompanyYearly/PanelElements/RowValue/RowValue';
import {
    PanelColumn,
    PanelRow,
    PanelTable,
    TableField
} from '../Panel/PanelTable/PanelTable';

import {
    ExtendedFieldsSchema,
    ExtendedSchemaField,
    FieldsSchema
} from '@assets/model/FieldsSchema';
import {
    RuleType,
    ScoreFieldsGroup,
    ScoreUtils,
    SectionNameTypes,
    useScoreStore
} from '@assets/model/ScoreUtils';
import { assert } from 'console';
import { PanelTitle } from '../Panel/PanelTitle/PanelTitle';
import { ScrollPanel } from '../Panel/ScrollPanel/ScrollPanel';
import { ScoreLineGreaterThan } from '../ScoreLine/ScoreLineGreaterThan';
import {
    ScoreLinePosition,
    ScoreLineRanged
} from '../ScoreLine/ScoreLineRanged';
import { ScoreLineYesNo } from '../ScoreLine/ScoreLineYesNo';
import { SectionIcon } from '../SectionIcons/SectionIcon';
import { Flex } from '../UI/Flex/Flex';
import * as style from './DealScoreBreakdown.module.less';
import { useState } from 'react';
import Checkbox from '../UI/Checkbox/Checkbox';

export const DealScoreBreakdown = observer(() => {
    const store = useStore();
    const [showNonUsed, setShowNonUsed] = useState(false);
    if (store.selectedReportId == '' || store.query == null) return null;

    const companyId = store.selectedReportId;
    const q = store.query;
    const scoreUtils: ScoreUtils = new ScoreUtils(q);

    const getLabel = (fieldName: string) => {
        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];

        if (e.t == '%') {
            return RowLabel.percent_label;
        } else {
            return RowLabel.label;
        }
    };
    const getFormatter = (fieldName: string) => {
        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];
        if (e.t == '%') {
            if (e.scale == 100) {
                return formatPercentNoMultiply;
            }

            return formatPercent;
        } else if (e.t == 'bool') {
            return null;
        } else {
            return formatDollar;
        }
    };

    var scoreStore = useScoreStore();
    const fieldsGroup = scoreStore.SCORE_FIELDS_GROUPS;

    type RuleData = {
        sectionId: SectionNameTypes;
        fieldName: string;
        val: any;
        rule: RuleType;
        ruleScore: number | null;
        scoreWeight: number;
        isUsed: boolean;
    };

    const getSectionIdAndFieldNameByRow = (row: TableField) => {
        const split = row.source.split('.');
        const sectionId = split[0] as SectionNameTypes;
        const fieldName = split[1];
        return { sectionId, fieldName };
    };

    const getRuleData = (row: TableField): RuleData => {
        const { sectionId, fieldName } = getSectionIdAndFieldNameByRow(row);
        const rule = ScoreUtils.get_score_rule_by_fieldname(
            sectionId,
            fieldName
        );
        const rawValue = scoreUtils.get_extended_float_for_scoring(rule);
        var ruleScore = 0;
        var isUsed = false;
        if (scoreUtils.is_rule_used_for_scoring(rule)) {
            ruleScore = scoreUtils.do_rule_score(rule);
            isUsed = true;
        }

        const scoreWeight = scoreUtils.get_rule_weight(sectionId, rule);

        return {
            sectionId,
            fieldName,
            val: rawValue,
            rule,
            ruleScore,
            scoreWeight,
            isUsed
        };
    };

    const getRangedScore = (data: RuleData) => {
        const { val, rule, ruleScore, fieldName } = data;
        const isScored = scoreUtils.is_rule_used_for_scoring(rule);
        var scoreEntries = [];
        var cls = '';
        if (isScored) {
            const score_not_scaled = scoreUtils.do_rule_score(rule, false);
            var scoreType: ScoreLinePosition = 'in-range';
            if (score_not_scaled > 1) {
                scoreType = 'over';
            } else if (score_not_scaled < 0) {
                scoreType = 'under';
            }

            const scoreEntry = {
                score: ruleScore,
                type: scoreType
            };
            scoreEntries.push(scoreEntry);
        } else {
            cls = style.notScoredGray;
        }

        const formatter = getFormatter(fieldName) ?? noFormatRaw;
        return (
            <>
                <ScoreLineRanged
                    className={cls}
                    entries={scoreEntries}
                    display={{
                        min: formatter(rule.range[rule.inverse_score ? 1 : 0]),
                        max: formatter(rule.range[rule.inverse_score ? 0 : 1]),
                        centerText: rule.inverse_score
                            ? '(lower is better)'
                            : '',
                        value: formatter(val)
                    }}
                />
            </>
        );
    };

    const getBooleanScore = (data: RuleData) => {
        const { ruleScore } = data;
        var didScore = null;
        if (data.isUsed) {
            didScore = ruleScore > 0;
        }
        return <ScoreLineYesNo didScore={didScore} />;
    };
    const getGreaterThanScore = (data: RuleData) => {
        const { val, rule, ruleScore, fieldName } = data;
        const entries = [];
        var cls = '';
        if (data.isUsed) {
            const scoreEntry = {
                didScore: ruleScore > 0,
                rawValue: val
            };
            entries.push(scoreEntry);
        } else {
            cls = style.notScoredGray;
        }

        const formatter = getFormatter(fieldName) ?? noFormatRaw;

        var threshold_label = formatter(rule.threshold);
        if (rule.threshold_display != null) {
            threshold_label = rule.threshold_display;
        }

        return (
            <>
                <ScoreLineGreaterThan
                    className={cls}
                    rule={rule}
                    entries={entries}
                    display={{
                        center: threshold_label,
                        min: formatter(rule.range[0]),
                        max: formatter(rule.range[1]),
                        value: formatter(val)
                    }}
                />
            </>
        );
    };

    const columns: PanelColumn[] = [
        {
            column: {
                source: null,
                display: 'Score Visualized'
            },
            overrideValue: (row: TableField, col: TableField) => {
                return null;
            },

            element: (row: PanelRow, col: PanelColumn, data: any) => {
                const ruleData = getRuleData(row.field);

                if (ruleData.val == 'N/A' && !showNonUsed) {
                    return null;
                }

                const t = ruleData.rule.check;
                var el = null;
                if (t == 'range') {
                    el = getRangedScore(ruleData);
                } else if (t == 'greater_than') {
                    el = getGreaterThanScore(ruleData);
                } else if (t == 'boolean') {
                    el = getBooleanScore(ruleData);
                }
                return (
                    <Flex justify="center" alignItems="center" fillCell={true}>
                        {el}
                    </Flex>
                );
            }
        },
        {
            column: {
                source: null,
                display: 'Percentile'
            },
            overrideValue: (row: TableField, col: TableField) => {
                const { ruleScore, isUsed } = getRuleData(row);
                if (!isUsed) {
                    return '-';
                }
                return ruleScore != null ? formatPercent(ruleScore) : '-';
            },

            element: RowValue.value
        },
        {
            column: {
                source: null,
                display: 'Weight'
            },
            overrideValue: (row: TableField, col: TableField) => {
                const { scoreWeight, ruleScore, isUsed } = getRuleData(row);
                if (!isUsed) {
                    return '-';
                }
                return `${formatPercent(ruleScore * scoreWeight).replace('%', '')}/${formatPercent(scoreWeight)}`;
            },

            element: RowValue.percent
        }
    ];

    const fieldsToRows = (group: ScoreFieldsGroup) => {
        var rows = [];
        group.fields.forEach(fieldName => {
            const rule = ScoreUtils.get_score_rule_by_fieldname(
                group.sectionId,
                fieldName
            );

            if (rule.check == 'none' || rule.disabled) {
                return;
            }

            if (!scoreUtils.is_rule_used_for_scoring(rule) && !showNonUsed) {
                return;
            }

            const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];
            assert(
                e != null,
                `Field ${fieldName} not found in fieldsNameLookup`
            );

            var display = e.display;
            if (e.dynDisplay) {
                display = q.formatExtendedValueDisplay(e.dynDisplay);
            }

            if (e.dynDisplayPrefix) {
                const dynDisp = e.dynDisplayPrefix.replace(
                    '$DATA_PICK_KEY',
                    scoreUtils.get_datapick_key_for_rule(rule) ?? 'N/A'
                );
                display = dynDisp + display;
            }
            rows.push({
                field: {
                    source: `${group.sectionId}.${fieldName}`,
                    display: display
                },

                label: getLabel(fieldName)
            });
        });

        return rows;
    };

    const groupTables = fieldsGroup.map((group, i) => {
        const groupRows = fieldsToRows(group);
        if (groupRows.length == 0) {
            return (
                <div key={'nodata_' + i}>
                    <PanelTitle
                        title={ScoreUtils.get_section_name(group.sectionId)}
                        containerClassName={style.panelTitle}
                        className={style.panelTitleText}
                    >
                        <SectionIcon
                            section={group.sectionId}
                            className={style.sectionIcon}
                        />
                    </PanelTitle>
                    <Flex justify="center">
                        <div className={style.noDataAvail}>
                            No Data Available
                        </div>
                    </Flex>
                </div>
            );
        }

        const sectionWorthIn100 =
            (group.section_worth /
                scoreUtils.get_max_points_based_on_sections_used()) *
            100;

        var points =
            scoreUtils.calcSectionPercentileById(group.sectionId) *
            sectionWorthIn100;
        if (isNaN(points)) {
            points = 0;
        }
        return (
            <div key={group.sectionId}>
                <PanelTitle
                    title={`${ScoreUtils.get_section_name(group.sectionId)} (${points.toFixed(0)}/${sectionWorthIn100.toFixed(0)}%)`}
                    containerClassName={style.panelTitle}
                    className={style.panelTitleText}
                >
                    <SectionIcon
                        section={group.sectionId}
                        className={style.sectionIcon}
                    />
                </PanelTitle>
                <PanelTable
                    columns={columns}
                    rows={groupRows}
                    disable_round_edges={true}
                    className={style.panelTableStyle}
                />
            </div>
        );
    });

    return (
        <>
            <ScrollPanel>
                <Checkbox
                    className={style.showNonUsedCheckbox}
                    checked={showNonUsed}
                    onChange={newState => {
                        setShowNonUsed(newState);
                    }}
                    label="Show Inactive Metrics"
                />
                {groupTables}
            </ScrollPanel>
        </>
    );
});
